import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { AlertTriangle, Info, Send, Clock, Plus, Trash2, Edit2, ExternalLink } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { collection, onSnapshot, query, orderBy, where, addDoc, updateDoc, doc, deleteDoc, writeBatch } from 'firebase/firestore';
import { UserProfile, ChatMessage, Announcement } from '../types';
import { format } from 'date-fns';
import { safeFormatDate } from '../lib/utils';
import { isSystemAdmin } from '../lib/admin';
import EmojiPicker, { Theme, EmojiClickData } from 'emoji-picker-react';

const CUSTOM_EMOJIS: Record<string, string> = {
  'combadge': 'emojis/combadge.jpg',
  'enterprise': 'emojis/enterprise.jpg',
  'phaser': 'emojis/phaser.jpg',
  'borg-cube': 'emojis/borg-cube.jpg'
};

const renderMessageText = (text: string) => {
  const parts = text.split(/(:\w+-?\w*:)/g);
  return parts.map((part, i) => {
    if (part.startsWith(':') && part.endsWith(':')) {
      const code = part.slice(1, -1);
      if (CUSTOM_EMOJIS[code]) {
        return <img key={i} src={`${import.meta.env.BASE_URL}${CUSTOM_EMOJIS[code]}`} alt={part} className="inline-block w-6 h-6 mx-1 align-bottom rounded-sm" />;
      }
    }
    return <span key={i}>{part}</span>;
  });
};

interface CommunicationsProps {
  currentUserProfile: UserProfile | null;
  allUsers: UserProfile[];
  hasNewAnnouncements: boolean;
  hasNewTenForward: boolean;
  onClearAnnouncements: () => void;
  onClearTenForward: () => void;
}

const Communications = React.memo(function Communications({ 
  currentUserProfile, 
  allUsers,
  hasNewAnnouncements,
  hasNewTenForward,
  onClearAnnouncements,
  onClearTenForward
}: CommunicationsProps) {
  const [activeChannel, setActiveChannel] = useState<'announcements' | 'general' | 'release_notes'>('announcements');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editMessageText, setEditMessageText] = useState('');

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isEditingAnnouncements, setIsEditingAnnouncements] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [announcementForm, setAnnouncementForm] = useState<Partial<Announcement>>({
    title: '', content: '', date: '', type: 'info', sender: 'COMMAND'
  });
  
  const isAdmin = isSystemAdmin(currentUserProfile);
  
  useEffect(() => {
    const q = query(collection(db, 'announcements'), orderBy('timestamp', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setAnnouncements(snap.docs.map(d => ({ id: d.id, ...d.data() } as Announcement)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'announcements');
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (activeChannel === 'announcements' || activeChannel === 'release_notes') {
      onClearAnnouncements();
    } else if (activeChannel === 'general') {
      onClearTenForward();
    }
    
    if (activeChannel !== 'general') return;

    const q = query(
      collection(db, 'messages'),
      where('channelId', '==', activeChannel),
      orderBy('timestamp', 'asc')
    );

    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() } as ChatMessage));
      setMessages(data);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'messages');
    });

    return () => unsub();
  }, [activeChannel]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() || !currentUserProfile?.uid) return;

    const newMsg = {
      channelId: activeChannel,
      userId: currentUserProfile.uid,
      userName: `${currentUserProfile.firstName} ${currentUserProfile.lastName}`,
      text: inputText.trim(),
      timestamp: new Date().toISOString()
    };

    setInputText('');

    try {
      await addDoc(collection(db, 'messages'), newMsg);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'messages');
    }
  };

  const handleUpdateMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMessageId || !editMessageText.trim()) return;
    
    try {
      await updateDoc(doc(db, 'messages', editingMessageId), { text: editMessageText.trim() });
      setEditingMessageId(null);
      setEditMessageText('');
    } catch (err) {
      handleFirestoreError(err, OperationType.WRITE, 'messages');
    }
  };

  const handleDeleteMessage = async (id: string) => {
    if (!isAdmin) return;
    if (confirm('Clear this message from the channel?')) {
      try {
        await deleteDoc(doc(db, 'messages', id));
      } catch (err) {
        handleFirestoreError(err, OperationType.DELETE, 'messages');
      }
    }
  };

  const handleClearAllMessages = async () => {
    if (!isAdmin) return;
    if (confirm('Are you sure you want to clear all messages in this channel? This cannot be undone.')) {
      try {
        const batch = writeBatch(db);
        messages.forEach(msg => {
          if (msg.id) {
            batch.delete(doc(db, 'messages', msg.id));
          }
        });
        await batch.commit();
        toast.success('Channel log cleared');
      } catch (err) {
        handleFirestoreError(err, OperationType.DELETE, 'messages');
      }
    }
  };

  const renderChat = (channelLabel: string) => (
    <div className="flex flex-col h-[600px] bg-[var(--background)] border-2 border-lcars-blue rounded-xl overflow-hidden mt-4">
      <div className="bg-lcars-blue text-black p-4 lcars-text-large flex justify-between items-center">
        <span>{channelLabel} LOG</span>
        <div className="flex items-center gap-4">
          {isAdmin && (
            <button 
              onClick={handleClearAllMessages}
              className="bg-lcars-red hover:bg-white text-[var(--foreground)] hover:text-black font-bold uppercase text-[0.65rem] px-3 py-1 rounded-full flex items-center gap-1 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> CLEAR ALL CHATS
            </button>
          )}
          <span className="text-sm font-sans tracking-normal opacity-80 hidden sm:flex items-center gap-1">
            <Clock className="w-4 h-4" />
            MESSAGES ARE END-TO-END ENCRYPTED
          </span>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages.map(msg => {
          const isMe = msg.userId === currentUserProfile?.uid;
          const isEditing = editingMessageId === msg.id;
          const canModerate = isMe || isAdmin;
          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'} group`}>
              <div className={`max-w-[80%] rounded-xl px-4 py-3 relative ${isMe ? 'bg-lcars-gold/20 border border-lcars-gold' : 'bg-white/5 border border-white/20'}`}>
                
                {/* Actions overlay */}
                <div className={`absolute -top-3 ${isMe ? 'right-2' : 'left-2'} flex lg:hidden lg:group-hover:flex gap-1 bg-[var(--background)] rounded p-1 border border-white/20 z-10 shadow-lg`}>
                  {canModerate && !isEditing && (
                    <button 
                      onClick={() => { setEditingMessageId(msg.id!); setEditMessageText(msg.text); }}
                      className="text-[var(--foreground)]/50 hover:text-lcars-gold p-1"
                      title="Edit Transmission"
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                  )}
                  {isAdmin && (
                    <button 
                      onClick={() => handleDeleteMessage(msg.id!)}
                      className="text-[var(--foreground)]/50 hover:text-lcars-red p-1"
                      title="Clear Transmission (Admin)"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-bold uppercase tracking-widest ${isMe ? 'text-lcars-gold' : 'text-lcars-blue'}`}>
                    {msg.userName}
                  </span>
                  <span className="text-[0.6rem] text-[var(--foreground)]/40">
                    {safeFormatDate(msg.timestamp, 'yyyy.MM.dd HH:mm', '00:00')}
                  </span>
                </div>

                {isEditing ? (
                  <form onSubmit={handleUpdateMessage} className="mt-2 flex flex-col gap-2 min-w-[200px]">
                    <textarea 
                      className="bg-[var(--background)]/50 border border-white/20 text-[var(--foreground)] font-sans tracking-normal p-2 text-sm rounded outline-none resize-none normal-case"
                      rows={3}
                      value={editMessageText}
                      onChange={e => setEditMessageText(e.target.value)}
                    />
                    <div className="flex justify-end gap-2">
                      <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-[var(--foreground)]/50 text-[0.6rem]" onClick={() => setEditingMessageId(null)}>
                        CANCEL
                      </Button>
                      <Button type="submit" size="sm" className="bg-lcars-gold hover:bg-white text-black h-6 px-2 text-[0.6rem]">
                        SAVE
                      </Button>
                    </div>
                  </form>
                ) : (
                  <p className="text-[var(--foreground)]/90 font-sans tracking-normal whitespace-pre-wrap break-words normal-case">{renderMessageText(msg.text)}</p>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 bg-white/5 border-t border-white/10 flex flex-col gap-2">
        {/* Star Trek Quick Emoji Tray */}
        <div className="flex items-center gap-2 pb-1 text-xs overflow-x-auto">
          <span className="text-[0.65rem] uppercase font-bold text-lcars-gold tracking-widest shrink-0">
            ⭐ STAR TREK:
          </span>
          <button 
            type="button" 
            onClick={() => setInputText(prev => prev + '🖖')} 
            title="Vulcan Salute" 
            className="hover:scale-125 transition-transform px-1.5 py-1 bg-black/40 rounded border border-white/10 hover:border-lcars-gold text-base"
          >
            🖖
          </button>
          <button 
            type="button" 
            onClick={() => setInputText(prev => prev + ':combadge:')} 
            title="Starfleet Combadge (:combadge:)" 
            className="hover:scale-125 transition-transform px-1.5 py-1 bg-black/40 rounded border border-white/10 hover:border-lcars-gold flex items-center justify-center"
          >
            <img src={`${import.meta.env.BASE_URL}emojis/combadge.jpg`} alt="Combadge" className="w-5 h-5 rounded-sm object-contain" />
          </button>
          <button 
            type="button" 
            onClick={() => setInputText(prev => prev + ':enterprise:')} 
            title="USS Enterprise (:enterprise:)" 
            className="hover:scale-125 transition-transform px-1.5 py-1 bg-black/40 rounded border border-white/10 hover:border-lcars-gold flex items-center justify-center"
          >
            <img src={`${import.meta.env.BASE_URL}emojis/enterprise.jpg`} alt="Enterprise" className="w-5 h-5 rounded-sm object-contain" />
          </button>
          <button 
            type="button" 
            onClick={() => setInputText(prev => prev + ':phaser:')} 
            title="Phaser (:phaser:)" 
            className="hover:scale-125 transition-transform px-1.5 py-1 bg-black/40 rounded border border-white/10 hover:border-lcars-gold flex items-center justify-center"
          >
            <img src={`${import.meta.env.BASE_URL}emojis/phaser.jpg`} alt="Phaser" className="w-5 h-5 rounded-sm object-contain" />
          </button>
          <button 
            type="button" 
            onClick={() => setInputText(prev => prev + ':borg-cube:')} 
            title="Borg Cube (:borg-cube:)" 
            className="hover:scale-125 transition-transform px-1.5 py-1 bg-black/40 rounded border border-white/10 hover:border-lcars-gold flex items-center justify-center"
          >
            <img src={`${import.meta.env.BASE_URL}emojis/borg-cube.jpg`} alt="Borg Cube" className="w-5 h-5 rounded-sm object-contain" />
          </button>
        </div>

        <form onSubmit={handleSendMessage} className="flex gap-2 relative">
          <div className="relative flex-1 flex gap-2">
            <Input 
              className="flex-1 bg-[var(--background)] border-white/20 text-[var(--foreground)] placeholder:text-[var(--foreground)]/30 font-sans tracking-normal normal-case"
              placeholder="Enter transmission in Ten Forward..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
            />
            <Button 
              type="button" 
              variant="outline" 
              className="bg-[var(--background)] border-white/20 text-lcars-blue hover:text-[var(--foreground)]"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              title="Open Emoji Picker (includes Star Trek emojis)"
            >
              😀
            </Button>
            {showEmojiPicker && (
              <div ref={emojiPickerRef} className="absolute bottom-12 right-0 z-50 shadow-xl border-2 border-lcars-blue rounded-xl overflow-hidden">
                <EmojiPicker 
                  theme={Theme.DARK}
                  onEmojiClick={(emojiData: EmojiClickData) => {
                    const code = emojiData.isCustom ? `:${(emojiData as any).id || emojiData.unified}:` : emojiData.emoji;
                    setInputText(prev => prev + code);
                  }}
                  customEmojis={[
                    {
                      id: 'startrek',
                      names: ['Star Trek', 'Ten Forward'],
                      emojis: [
                        { id: 'combadge', names: ['combadge', 'starfleet', 'badge', 'communicator'], imgUrl: `${import.meta.env.BASE_URL}emojis/combadge.jpg` },
                        { id: 'enterprise', names: ['enterprise', 'uss-enterprise', 'starship', 'ncc-1701'], imgUrl: `${import.meta.env.BASE_URL}emojis/enterprise.jpg` },
                        { id: 'phaser', names: ['phaser', 'weapon', 'type-2-phaser'], imgUrl: `${import.meta.env.BASE_URL}emojis/phaser.jpg` },
                        { id: 'borg-cube', names: ['borg-cube', 'borg', 'cube', 'assimilation'], imgUrl: `${import.meta.env.BASE_URL}emojis/borg-cube.jpg` }
                      ]
                    }
                  ] as any}
                />
              </div>
            )}
          </div>
          <Button type="submit" disabled={!inputText.trim()} className="bg-lcars-gold hover:bg-lcars-gold/80 text-black">
            <Send className="w-4 h-4 mr-2" /> SEND
          </Button>
        </form>
      </div>
    </div>
  );

  const handleSaveAnnouncement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) return;

    try {
      const payload = {
        title: announcementForm.title,
        content: announcementForm.content,
        date: announcementForm.date || new Date().toISOString().split('T')[0].replace(/-/g, '.'),
        type: activeChannel === 'release_notes' ? 'release_note' : (announcementForm.type || 'info'),
        sender: announcementForm.sender || 'COMMAND',
        timestamp: new Date().toISOString()
      };

      if (editingId) {
        await updateDoc(doc(db, 'announcements', editingId), payload);
      } else {
        await addDoc(collection(db, 'announcements'), payload);
      }
      setEditingId(null);
      setAnnouncementForm({ title: '', content: '', date: '', type: 'info', sender: 'COMMAND' });
      setIsEditingAnnouncements(false);
    } catch (err) {
      handleFirestoreError(err, OperationType.WRITE, 'announcements');
    }
  };

  const handleDeleteAnnouncement = async (id: string) => {
    if (!isAdmin) return;
    if (confirm('Delete this announcement?')) {
      try {
        await deleteDoc(doc(db, 'announcements', id));
      } catch (err) {
        handleFirestoreError(err, OperationType.DELETE, 'announcements');
      }
    }
  };

  const renderAnnouncementForm = () => {
    if (!isEditingAnnouncements || !isAdmin) return null;
    const isReleaseNote = activeChannel === 'release_notes';
    return (
      <form onSubmit={handleSaveAnnouncement} className="bg-white/5 border border-lcars-blue p-6 rounded-lg mb-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label htmlFor="ann-title" className="text-[0.6rem] text-lcars-blue font-bold uppercase">Title</label>
            <Input 
              id="ann-title"
              required 
              value={announcementForm.title} 
              onChange={e => setAnnouncementForm({...announcementForm, title: e.target.value})}
              className="bg-[var(--background)] border-lcars-blue text-[var(--foreground)] normal-case"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="ann-date" className="text-[0.6rem] text-lcars-blue font-bold uppercase">Stardate (e.g. 2026.04.14)</label>
            <Input 
              id="ann-date"
              value={announcementForm.date} 
              onChange={e => setAnnouncementForm({...announcementForm, date: e.target.value})}
              className="bg-[var(--background)] border-lcars-blue text-[var(--foreground)]" 
              placeholder="Leave blank for today"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="ann-sender" className="text-[0.6rem] text-lcars-blue font-bold uppercase">Sender</label>
            <Input 
              id="ann-sender"
              required 
              value={announcementForm.sender} 
              onChange={e => setAnnouncementForm({...announcementForm, sender: e.target.value})}
              className="bg-[var(--background)] border-lcars-blue text-[var(--foreground)] normal-case"
            />
          </div>
          {!isReleaseNote && (
            <div className="space-y-1">
              <label htmlFor="ann-type" className="text-[0.6rem] text-lcars-blue font-bold uppercase">Type</label>
              <select 
                id="ann-type"
                value={announcementForm.type} 
                onChange={e => setAnnouncementForm({...announcementForm, type: e.target.value})}
                className="h-8 w-full rounded border border-lcars-blue bg-[var(--background)] px-2 py-1 text-sm text-[var(--foreground)] outline-none"
              >
                <option value="info">INFO</option>
                <option value="alert">ALERT</option>
              </select>
            </div>
          )}
        </div>
        <div className="space-y-1">
          <label htmlFor="ann-content" className="text-[0.6rem] text-lcars-blue font-bold uppercase">Content</label>
          <textarea 
            id="ann-content"
            required 
            value={announcementForm.content} 
            onChange={e => setAnnouncementForm({...announcementForm, content: e.target.value})}
            className="w-full bg-[var(--background)] border border-lcars-blue text-[var(--foreground)] rounded p-2 text-sm min-h-[100px] outline-none normal-case"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="submit" className="bg-lcars-gold text-black">
            {editingId ? (isReleaseNote ? 'UPDATE RELEASE NOTE' : 'UPDATE ANNOUNCEMENT') : (isReleaseNote ? 'SEND RELEASE NOTE' : 'SEND ANNOUNCEMENT')}
          </Button>
        </div>
      </form>
    );
  };

  const renderGoogleChatLink = (channelName: string, description: string) => (
    <div className="flex flex-col h-[600px] bg-[var(--background)] border-2 border-lcars-blue rounded-xl overflow-hidden mt-4 items-center justify-center p-8 text-center space-y-6">
      <div className="bg-lcars-blue/20 p-6 rounded-full inline-block">
        <Send className="w-16 h-16 text-lcars-blue" />
      </div>
      <div className="space-y-2">
        <h3 className="text-2xl font-bold text-lcars-blue uppercase tracking-widest">{channelName}</h3>
        <p className="text-[var(--foreground)]/70 max-w-md mx-auto">{description}</p>
      </div>
      <a 
        href="https://chat.google.com/" 
        target="_blank" 
        rel="noopener noreferrer"
        className="lcars-pill bg-lcars-gold text-black flex items-center gap-2 px-8 py-3 font-bold uppercase tracking-widest hover:bg-white transition-colors"
      >
        <ExternalLink className="w-5 h-5" /> OPEN GOOGLE CHAT
      </a>
    </div>
  );

  const onlineUsers = allUsers.filter(u => {
    if (!u.lastActive) return false;
    return (new Date().getTime() - new Date(u.lastActive).getTime()) < 10 * 60 * 1000;
  });

  const regularAnnouncements = announcements.filter(a => a.type !== 'release_note');
  const releaseNotes = announcements.filter(a => a.type === 'release_note');

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <div className="flex-1 space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
          <span className="text-xl font-bold text-lcars-gold uppercase">Subspace Comms</span>
          {isAdmin && (activeChannel === 'announcements' || activeChannel === 'release_notes') && (
            <Button
              onClick={() => {
                setIsEditingAnnouncements(!isEditingAnnouncements);
                setEditingId(null);
                setAnnouncementForm({ 
                  title: '', 
                  content: '', 
                  date: '', 
                  type: activeChannel === 'release_notes' ? 'release_note' : 'info', 
                  sender: 'COMMAND' 
                });
              }}
              className="bg-lcars-blue hover:bg-white text-black font-bold uppercase text-xs rounded-full"
            >
              {isEditingAnnouncements ? 'CANCEL EDIT' : activeChannel === 'release_notes' ? 'MANAGE RELEASE NOTES' : 'MANAGE ANNOUNCEMENTS'}
            </Button>
          )}
        </div>

        <Tabs defaultValue="announcements" value={activeChannel} onValueChange={(v: any) => setActiveChannel(v)} className="w-full">
          <TabsList className="bg-white/5 border border-white/10 w-full justify-start rounded-full p-1 mb-6 uppercase h-auto flex flex-wrap">
            <TabsTrigger value="announcements" className="rounded-full data-[state=active]:bg-lcars-gold data-[state=active]:text-black text-[0.6rem] sm:text-xs py-2 px-4 sm:px-6">
              Announcements {hasNewAnnouncements && '*'}
            </TabsTrigger>
            <TabsTrigger value="general" className="rounded-full data-[state=active]:bg-lcars-purple data-[state=active]:text-[var(--foreground)] text-[0.6rem] sm:text-xs py-2 px-4 sm:px-6">
              Ten Forward {hasNewTenForward && '*'}
            </TabsTrigger>
            <TabsTrigger value="release_notes" className="rounded-full data-[state=active]:bg-lcars-pink data-[state=active]:text-black text-[0.6rem] sm:text-xs py-2 px-4 sm:px-6">
              Release Notes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="announcements">
            {renderAnnouncementForm()}

            <div className="bg-[var(--background)] border-l-4 lg:border-l-8 border-lcars-gold pl-3 lg:pl-6 py-2">
              <div className="space-y-6">
                {regularAnnouncements.length === 0 && !isEditingAnnouncements && (
                  <div className="text-[var(--foreground)]/40 text-center py-8 font-bold tracking-widest uppercase">No announcements found.</div>
                )}
                {regularAnnouncements.map((msg) => (
                  <div key={msg.id} className="bg-white/5 border-l-4 border-lcars-gold p-6 rounded-r-full hover:bg-white/10 transition-all group relative">
                    {isAdmin && (
                      <div className="absolute top-4 right-4 flex gap-2 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                        <button onClick={() => {
                          setAnnouncementForm(msg);
                          setEditingId(msg.id!);
                          setIsEditingAnnouncements(true);
                        }} className="text-lcars-blue hover:text-[var(--foreground)]"><Edit2 className="w-4 h-4" /></button>
                        <button onClick={() => handleDeleteAnnouncement(msg.id!)} className="text-lcars-red hover:text-[var(--foreground)]"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 pr-16">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-full ${msg.type === 'alert' ? 'bg-lcars-red text-[var(--foreground)]' : 'bg-lcars-blue text-black'}`}>
                          {msg.type === 'alert' ? <AlertTriangle className="w-4 h-4" /> : <Info className="w-4 h-4" />}
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-lcars-gold group-hover:text-[var(--foreground)] transition-colors">{msg.title}</h3>
                          <p className="text-[0.6rem] text-[var(--foreground)]/40 font-bold tracking-widest uppercase">FROM: {msg.sender} | STARDATE: {msg.date}</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-[var(--foreground)]/80 leading-relaxed font-medium">
                      {msg.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="general">{renderChat('TEN FORWARD')}</TabsContent>

          <TabsContent value="release_notes">
            {renderAnnouncementForm()}

            <div className="bg-[var(--background)] border-l-4 lg:border-l-8 border-lcars-pink pl-3 lg:pl-6 py-2">
              <div className="space-y-6">
                {releaseNotes.length === 0 && !isEditingAnnouncements && (
                  <div className="text-[var(--foreground)]/40 text-center py-8 font-bold tracking-widest uppercase">No release notes found.</div>
                )}
                {releaseNotes.map((msg) => (
                  <div key={msg.id} className="bg-white/5 border-l-4 border-lcars-pink p-6 rounded-r-full hover:bg-white/10 transition-all group relative">
                    {isAdmin && (
                      <div className="absolute top-4 right-4 flex gap-2 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                        <button onClick={() => {
                          setAnnouncementForm(msg);
                          setEditingId(msg.id!);
                          setIsEditingAnnouncements(true);
                        }} className="text-lcars-blue hover:text-[var(--foreground)]"><Edit2 className="w-4 h-4" /></button>
                        <button onClick={() => handleDeleteAnnouncement(msg.id!)} className="text-lcars-red hover:text-[var(--foreground)]"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 pr-16">
                      <div className="flex items-center gap-4">
                        <div className="p-2 rounded-full bg-lcars-pink text-black">
                          <Info className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-lcars-pink group-hover:text-[var(--foreground)] transition-colors normal-case">{msg.title}</h3>
                          <p className="text-[0.6rem] text-[var(--foreground)]/40 font-bold tracking-widest uppercase">FROM: {msg.sender} | STARDATE: {msg.date}</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-[var(--foreground)]/80 leading-relaxed font-medium normal-case">
                      {msg.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Online Personnel Sidebar */}
      <div className="w-full lg:w-72 shrink-0">
        <div className="bg-[var(--background)] border-2 border-lcars-pink rounded-xl overflow-hidden">
          <div className="bg-lcars-pink text-black p-3 text-sm font-bold uppercase flex justify-between items-center">
            <span>ACTIVE MONITORS</span>
            <span className="bg-[var(--background)] text-lcars-pink px-2 py-0.5 rounded-full text-xs animate-pulse">
              {onlineUsers.length} ONLINE
            </span>
          </div>
          <div className="p-4 space-y-3">
            {onlineUsers.length === 0 ? (
              <div className="text-[var(--foreground)]/40 text-xs tracking-widest">NO LIFEFORMS DETECTED</div>
            ) : (
              onlineUsers.map(u => (
                <div key={u.id} className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center font-bold text-[var(--foreground)] text-xs border border-white/20">
                      {u.firstName?.[0]}{u.lastName?.[0]}
                    </div>
                    <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-lcars-blue rounded-full border border-black animate-pulse"></div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-lcars-gold truncate max-w-[160px]">
                      {u.firstName} {u.lastName}
                    </span>
                    <span className="text-[0.6rem] text-[var(--foreground)]/50 tracking-widest">
                      {u.currentRank || 'CREW'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default Communications;
